import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { LogOut, LayoutGrid, Sun, Moon, Menu, Home as HomeIcon, ShieldCheck, Building2, Info, Users, Mail } from "lucide-react";

export const TAGLINE = "Your Trusted Academic Proof.";

export function BrandLogo({ size = 32, showText = true }) {
  return (
    <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-logo">
      <img
        src="/certiseal-logo.png"
        alt="CertiSeal"
        style={{ width: size, height: size }}
        className="rounded-full object-contain transition-transform group-hover:scale-105 dark:bg-white dark:p-0.5"
      />
      {showText && (
        <div className="flex flex-col leading-none">
          <span className="font-display font-extrabold text-xl tracking-tight text-slate-900 dark:text-white">CertiSeal</span>
          <span className="text-[10px] font-medium tracking-[0.15em] uppercase text-slate-500 dark:text-slate-400 mt-0.5">{TAGLINE}</span>
        </div>
      )}
    </Link>
  );
}

// Public menu items — used by the hamburger nav sheet.
const PUBLIC_NAV = [
  { to: "/",          label: "Home",                  icon: HomeIcon,     testId: "menu-home" },
  { to: "/verify",    label: "Verify Credential",     icon: ShieldCheck,  testId: "menu-verify" },
  { to: "/registry",  label: "Institutions Registry", icon: Building2,    testId: "menu-registry" },
  { to: "/team",      label: "Our Team",              icon: Users,        testId: "menu-team" },
  { to: "/about",     label: "About Us",              icon: Info,         testId: "menu-about" },
  { to: "/contact",   label: "Contact Us",            icon: Mail,         testId: "menu-contact" },
];

export function PublicNavMenu({ variant = "light" }) {
  const [open, setOpen] = useState(false);
  const isDark = variant === "dark";
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={`h-10 w-10 rounded-full ${isDark ? "text-white hover:bg-white/10" : "text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
          data-testid="nav-menu-btn"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-80 bg-white dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800 p-0">
        <SheetHeader className="px-6 pt-8 pb-6 border-b border-slate-100 dark:border-slate-900">
          <SheetTitle className="text-left">
            <div className="flex items-center gap-2.5">
              <img src="/certiseal-logo.png" alt="" className="w-9 h-9 rounded-full object-contain dark:bg-white dark:p-0.5" />
              <div className="flex flex-col leading-none">
                <span className="font-display font-extrabold text-lg tracking-tight text-slate-900 dark:text-white">CertiSeal</span>
                <span className="text-[10px] font-medium tracking-[0.15em] uppercase text-slate-500 dark:text-slate-400 mt-0.5">{TAGLINE}</span>
              </div>
            </div>
          </SheetTitle>
        </SheetHeader>
        <nav className="px-3 py-4" data-testid="public-nav-list">
          {PUBLIC_NAV.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300"
                    : "text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900"
                }`
              }
              data-testid={testId}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
      </SheetContent>
    </Sheet>
  );
}

export function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const isDark = theme === "dark";
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      className="rounded-full h-9 w-9 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
      aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
      data-testid="theme-toggle-btn"
    >
      {isDark ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-slate-700" />}
    </Button>
  );
}

export function PublicHeader({ transparent = false }) {
  const { user } = useAuth();
  const menuVariant = transparent ? "dark" : "light";
  return (
    <header className={`sticky top-0 z-50 w-full ${transparent
      ? "bg-transparent"
      : "backdrop-blur-xl bg-white/80 dark:bg-slate-950/80 border-b border-slate-200/80 dark:border-slate-800/80"}`}>
      <div className="w-full max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <PublicNavMenu variant={menuVariant} />
        <div className="flex items-center gap-2">
          {!transparent && <ThemeToggle />}
          {user && user.role && (
            <Link to={user.role === "super_admin" ? "/admin" : user.role === "l2_admin" ? "/ops" : "/institution"}>
              <Button
                variant={transparent ? "outline" : "outline"}
                className={`rounded-md ${transparent ? "bg-white/10 text-white border-white/30 hover:bg-white/20" : "dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-800"}`}
                data-testid="nav-dashboard-btn"
              >
                <LayoutGrid className="w-4 h-4 mr-2" /> Dashboard
              </Button>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

export function DashboardLayout({ children, title, subtitle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <header className="sticky top-0 z-50 w-full backdrop-blur-xl bg-white/90 dark:bg-slate-950/90 border-b border-slate-200 dark:border-slate-800">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo />
            <span className="hidden sm:block text-xs font-semibold uppercase tracking-widest text-blue-600 dark:text-blue-400" data-testid="role-badge">
              {user?.role === "super_admin" ? "Super Admin" : user?.role === "l2_admin" ? "Operations" : "Institution"}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <div className="hidden sm:block text-right">
              <div className="text-sm font-medium text-slate-900 dark:text-slate-100" data-testid="user-name">{user?.name}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400" data-testid="user-email">{user?.email}</div>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn" className="dark:text-slate-200 dark:hover:bg-slate-800">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>
      <main className="w-full max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14">
        {title && (
          <div className="mb-10">
            <h1 className="font-display text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight" data-testid="page-title">{title}</h1>
            {subtitle && <p className="mt-2 text-base text-slate-600 dark:text-slate-400 max-w-2xl">{subtitle}</p>}
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
